<script setup lang="ts">
/**
 * Layers panel — grouped by type (Bug 4): Trace (top) · Ink · Sketch (bottom),
 * matching the on-canvas stack order. Sketch and ink are separate layer types,
 * up to 5 each; trace is the vector shapes. Top of each group's list = top of
 * that group's sub-stack. Clicking a raster layer makes it active AND switches
 * to its drawing mode.
 */
import {
  IconChevronDown,
  IconChevronUp,
  IconEraser,
  IconEye,
  IconEyeOff,
  IconLine,
  IconPencil,
  IconPlus,
  IconTrash,
  IconVectorBezier,
} from '@tabler/icons-vue';
import { computed, ref } from 'vue';
import { BASE_PALETTE_SIZE, GRAPHITE_INDEX, paletteHex } from '@/patterns/wallpaper';
import { MAX_SKETCH_LAYERS, useLayersStore } from '@/stores/layers';
import { useToolsStore } from '@/stores/tools';
import { useTraceEditor } from '@/composables/useTraceEditor';

const layers = useLayersStore();
const tools = useToolsStore();
const trace = useTraceEditor();

/** render top-of-stack first within each group */
const sketchDisplay = computed(() => [...layers.sketchLayers].reverse());
const traceLayersDisplay = computed(() => [...layers.traceLayers].reverse());

/** brief 12-vector_draw: 'outline' (default) is plotted once, at its drawn
 *  location; 'solid' replicates a simple stroke across the tessellation.
 *  Direct mutation, same as tl.visible/tl.opacity above. */
function toggleTraceDrawMode(tl: { drawMode?: 'outline' | 'solid' }): void {
  tl.drawMode = tl.drawMode === 'solid' ? 'outline' : 'solid';
}

// Delete-all: tap once to arm, tap again to confirm (same pattern the toolbar
// used before this moved here). Undoable, so the confirm gate is a courtesy
// against a stray tap rather than a last line of defence.
const confirmClearAll = ref(false);

function onClearAll(): void {
  if (!confirmClearAll.value) {
    confirmClearAll.value = true;
    setTimeout(() => (confirmClearAll.value = false), 2500);
    return;
  }
  confirmClearAll.value = false;
  layers.clearDrawing();
}

// Per-layer "clear content" (erase, keep the layer): same tap-to-arm /
// tap-again-to-confirm courtesy gate as delete-all above, keyed by layer id so
// only the tapped row is armed at a time.
const confirmClearId = ref<string | null>(null);
let clearTimer: ReturnType<typeof setTimeout> | undefined;

function onClearLayer(id: string, kind: 'raster' | 'trace'): void {
  if (confirmClearId.value !== id) {
    confirmClearId.value = id;
    clearTimeout(clearTimer);
    clearTimer = setTimeout(() => (confirmClearId.value = null), 2500);
    return;
  }
  clearTimeout(clearTimer);
  confirmClearId.value = null;
  if (kind === 'raster') layers.clearRasterLayer(id);
  else layers.clearTraceLayer(id);
}

// Trace layer colour picker: tapping the swatch opens the same 12-colour base
// palette (+ graphite) used elsewhere so each trace layer's colour can be set
// independently. Direct mutation of tl.stroke/tl.fill, same as the
// visible/opacity/drawMode controls beside it.
const TRACE_PALETTE = [...Array.from({ length: BASE_PALETTE_SIZE }, (_, i) => i), GRAPHITE_INDEX];
const swatchOpenId = ref<string | null>(null);

function toggleTraceSwatch(id: string): void {
  swatchOpenId.value = swatchOpenId.value === id ? null : id;
}

function pickTraceColour(tl: { stroke: string; fill?: string }, index: number): void {
  const hex = paletteHex(index, tools.isDark);
  tl.stroke = hex;
  tl.fill = `${hex}48`; // ~28% alpha, matching the faint completed-trace fill
  swatchOpenId.value = null;
}
</script>

<template>
  <div class="layer-panel">
    <header>
      <h2>Layers</h2>
      <button
        class="clear-all-btn"
        :class="{ danger: confirmClearAll }"
        :title="confirmClearAll ? 'Tap again to clear everything' : 'Clear all layers'"
        @click="onClearAll"
      >
        <span class="icon-stack" aria-hidden="true">
          <IconTrash :size="13" stroke-width="1.75" class="icon-back" />
          <IconTrash :size="13" stroke-width="1.75" class="icon-front" />
        </span>
        <span v-if="confirmClearAll">Sure?</span>
      </button>
    </header>

    <ul>
      <!-- Trace layers (vector shapes) at the top -->
      <li class="group-head" v-if="traceLayersDisplay.length > 0">
        <span>Trace</span>
      </li>
      <li
        v-for="(tl, i) in traceLayersDisplay"
        :key="tl.id"
        class="layer-row trace-layer"
        :class="{ active: tl.id === layers.editingTraceLayerId }"
      >
        <div class="row-top">
          <button
            class="icon-btn"
            :title="tl.visible ? 'Hide trace' : 'Show trace'"
            @click.stop="tl.visible = !tl.visible"
          >
            <IconEye v-if="tl.visible" :size="15" stroke-width="1.75" />
            <IconEyeOff v-else :size="15" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            :title="tl.drawMode === 'solid' ? 'Solid vector line — tap for coloured outline' : 'Coloured outline — tap for solid vector line'"
            @click.stop="toggleTraceDrawMode(tl)"
          >
            <IconLine v-if="tl.drawMode === 'solid'" :size="15" stroke-width="1.75" />
            <IconVectorBezier v-else :size="15" stroke-width="1.75" />
          </button>
          <span class="layer-name trace-name">
            <span class="trace-label">{{ tl.name }}</span>
            <span class="swatch-wrap">
              <button
                class="trace-color"
                type="button"
                title="Change trace colour"
                :style="{ backgroundColor: tl.stroke }"
                @click.stop="toggleTraceSwatch(tl.id)"
              ></button>
              <div v-if="swatchOpenId === tl.id" class="swatch-popup" @click.stop>
                <button
                  v-for="idx in TRACE_PALETTE"
                  :key="idx"
                  class="swatch-choice"
                  type="button"
                  :style="{ backgroundColor: paletteHex(idx, tools.isDark) }"
                  @click.stop="pickTraceColour(tl, idx)"
                ></button>
              </div>
              <div v-if="swatchOpenId === tl.id" class="swatch-backdrop" @click.stop="swatchOpenId = null"></div>
            </span>
          </span>
          <button
            class="icon-btn"
            title="Move up"
            :disabled="i === 0"
            @click.stop="layers.moveTraceLayer(tl.id, 'up')"
          >
            <IconChevronUp :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            title="Move down"
            :disabled="i === traceLayersDisplay.length - 1"
            @click.stop="layers.moveTraceLayer(tl.id, 'down')"
          >
            <IconChevronDown :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            title="Edit trace"
            :disabled="!!layers.editingTraceLayerId && layers.editingTraceLayerId !== tl.id"
            @click.stop="trace.editLayer(tl.id)"
          >
            <IconPencil :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            :class="{ danger: confirmClearId === tl.id }"
            :title="confirmClearId === tl.id ? 'Tap again to clear' : 'Clear trace content'"
            @click.stop="onClearLayer(tl.id, 'trace')"
          >
            <IconEraser :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            title="Delete trace layer"
            @click.stop="layers.deleteTraceLayer(tl.id)"
          >
            <IconTrash :size="14" stroke-width="1.75" />
          </button>
        </div>
        <input
          v-model.number="tl.opacity"
          class="opacity-slider"
          type="range"
          min="0"
          max="1"
          step="0.05"
          :aria-label="`${tl.name} opacity`"
          @click.stop
        />
      </li>

      <!-- Sketch layers at the bottom -->
      <li class="group-head">
        <span>Sketch</span>
        <button
          class="add-btn"
          :disabled="layers.sketchLayers.length >= MAX_SKETCH_LAYERS"
          title="Add sketch layer"
          @click="layers.addSketchLayer()"
        >
          <IconPlus :size="13" stroke-width="2" />
        </button>
      </li>
      <li
        v-for="(layer, i) in sketchDisplay"
        :key="layer.id"
        class="layer-row"
        :class="{ active: layer.id === layers.activeSketchLayerId }"
        @click="layers.selectRasterLayer(layer.id)"
      >
        <div class="row-top">
          <button
            class="icon-btn"
            :title="layer.visible ? 'Hide layer' : 'Show layer'"
            @click.stop="layer.visible = !layer.visible"
          >
            <IconEye v-if="layer.visible" :size="15" stroke-width="1.75" />
            <IconEyeOff v-else :size="15" stroke-width="1.75" />
          </button>
          <span class="layer-name">{{ layer.name }}</span>
          <button
            class="icon-btn"
            title="Move up"
            :disabled="i === 0"
            @click.stop="layers.moveRasterLayer(layer.id, 'up')"
          >
            <IconChevronUp :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            title="Move down"
            :disabled="i === sketchDisplay.length - 1"
            @click.stop="layers.moveRasterLayer(layer.id, 'down')"
          >
            <IconChevronDown :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            :class="{ danger: confirmClearId === layer.id }"
            :title="confirmClearId === layer.id ? 'Tap again to clear' : 'Clear layer content'"
            @click.stop="onClearLayer(layer.id, 'raster')"
          >
            <IconEraser :size="14" stroke-width="1.75" />
          </button>
          <button
            class="icon-btn"
            :disabled="layers.sketchLayers.length <= 1"
            title="Delete layer"
            @click.stop="layers.removeRasterLayer(layer.id)"
          >
            <IconTrash :size="14" stroke-width="1.75" />
          </button>
        </div>
        <input
          v-model.number="layer.opacity"
          class="opacity-slider"
          type="range"
          min="0"
          max="1"
          step="0.05"
          :aria-label="`${layer.name} opacity`"
          @click.stop
        />
      </li>
    </ul>
  </div>
</template>

<style scoped>
.layer-panel {
  padding: 12px;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.clear-all-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  min-height: 24px;
  background: transparent;
  border: 1px solid var(--ui-border);
  border-radius: 6px;
  color: var(--text-muted);
  font-size: 10px;
  cursor: pointer;
}

.clear-all-btn:hover {
  color: var(--text-primary);
  border-color: var(--accent);
}

.clear-all-btn.danger {
  border-color: var(--danger);
  color: var(--danger);
  background: color-mix(in srgb, var(--danger) 12%, transparent);
}

/* Two offset trash glyphs — "deletes every layer", not just one. */
.icon-stack {
  position: relative;
  width: 15px;
  height: 13px;
  flex-shrink: 0;
}

.icon-stack .icon-back,
.icon-stack .icon-front {
  position: absolute;
  top: 0;
}

.icon-stack .icon-back {
  left: 2px;
  opacity: 0.5;
}

.icon-stack .icon-front {
  left: 0;
}

h2 {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-muted);
  margin: 0;
}

.group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  padding: 2px 2px 0;
}

.group-head span {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-muted);
}

.empty-hint {
  font-size: 11px;
  color: var(--text-muted);
  opacity: 0.7;
  padding: 2px 4px;
}

.add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--ui-border);
  border-radius: 6px;
  cursor: pointer;
}

.add-btn:hover:not(:disabled) {
  border-color: var(--accent);
}

.add-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.layer-row {
  padding: 7px 8px;
  border: 1px solid var(--ui-border);
  border-radius: 8px;
  cursor: pointer;
}

.layer-row.active {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 10%, transparent);
}

.layer-row.trace-layer {
  cursor: default;
  background: color-mix(in srgb, var(--accent) 6%, transparent);
}

.row-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
}

.icon-btn:hover:not(:disabled) {
  color: var(--text-primary);
}

.icon-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.icon-btn.danger {
  color: var(--danger);
}

.layer-name {
  flex: 1;
  font-size: 12px;
  color: var(--text-primary);
}

.trace-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trace-label {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: 500;
}

.trace-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
  flex-shrink: 0;
  padding: 0;
  cursor: pointer;
}

.swatch-wrap {
  position: relative;
  display: inline-flex;
}

.swatch-popup {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 5px;
  padding: 7px;
  background: var(--ui-surface, var(--surface, #1b1922));
  border: 1px solid var(--ui-border);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgb(0 0 0 / 35%);
}

.swatch-choice {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, currentColor 25%, transparent);
  padding: 0;
  cursor: pointer;
}

.swatch-choice:hover {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

.swatch-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9;
}

.opacity-slider {
  width: 100%;
  accent-color: var(--accent);
  margin: 0;
}
</style>
